import type { ReactNode } from 'react'
import type { Quote, SectorRef, StockRow } from '@/lib/market/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowDown, ArrowUp, Search, Star, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { Segmented } from '@/components/market/segmented'
import { ChangeText } from '@/components/market/change'
import { useQuotes, usePriceFlash } from '@/components/market/live-provider'
import { useWatchlist } from '@/hooks/use-watchlist'
import Sparkline from '@/components/charts/sparkline'
import RangeBar from '@/components/market/range-bar'
import WatchButton from '@/components/market/watch-button'

import {
    formatCompact,
    formatKes,
    formatMultiple,
    formatNumber,
    formatPercent,
    formatPrice,
    formatSigned
} from '@/lib/market/format'

export type EquitiesView = 'prices' | 'performance' | 'fundamentals' | 'technicals'

type SortDirection = 'asc' | 'desc'

export interface EquityComputed {
    stock: StockRow
    quote: Quote
    cap: number
    pe: number | null
    dividendYield: number | null
    payout: number | null
    signal: string
}

interface Column {
    key: string
    label: string
    title?: string
    align?: 'left' | 'right'
    className?: string
    sort: (row: EquityComputed) => number | string | null
    render: (row: EquityComputed) => ReactNode
}

const VIEWS: Array<{ value: EquitiesView, label: string }> = [
    { value: 'prices', label: 'Prices' },
    { value: 'performance', label: 'Performance' },
    { value: 'fundamentals', label: 'Fundamentals' },
    { value: 'technicals', label: 'Technicals' }
]

export const signalFor = (stock: StockRow): string => {
    if (stock.rsi !== null && stock.rsi >= 70) return 'Overbought'
    if (stock.rsi !== null && stock.rsi <= 30) return 'Oversold'
    if ((stock.vsSma50 ?? 0) > 0 && (stock.vsSma200 ?? 0) > 0) return 'Uptrend'
    if ((stock.vsSma50 ?? 0) < 0 && (stock.vsSma200 ?? 0) < 0) return 'Downtrend'

    return 'Mixed'
}

const perfCell = (value: number | null): ReactNode => <ChangeText value={value} decimals={1} />

const COLUMNS: Record<EquitiesView, Column[]> = {
    prices: [
        { key: 'last', label: 'Last', align: 'right', sort: row => row.quote.last, render: row => <PriceCell value={row.quote.last} /> },
        { key: 'change', label: 'Chg', title: 'Change in KES', align: 'right', className: 'hidden md:table-cell', sort: row => row.quote.change, render: row => <ChangeText value={row.quote.change} percent={false} /> },
        { key: 'changePct', label: '% Chg', align: 'right', sort: row => row.quote.changePct, render: row => <ChangeText value={row.quote.changePct} /> },
        { key: 'high', label: 'High', align: 'right', className: 'hidden xl:table-cell', sort: row => row.quote.high, render: row => <span className='tabular-nums text-muted-foreground'>{formatPrice(row.quote.high)}</span> },
        { key: 'low', label: 'Low', align: 'right', className: 'hidden xl:table-cell', sort: row => row.quote.low, render: row => <span className='tabular-nums text-muted-foreground'>{formatPrice(row.quote.low)}</span> },
        { key: 'volume', label: 'Volume', align: 'right', className: 'hidden sm:table-cell', sort: row => row.quote.volume, render: row => <span className='tabular-nums'>{formatCompact(row.quote.volume)}</span> },
        { key: 'turnover', label: 'Turnover', align: 'right', className: 'hidden lg:table-cell', sort: row => row.quote.turnover, render: row => <span className='tabular-nums'>{formatKes(row.quote.turnover)}</span> },
        { key: 'cap', label: 'Mkt cap', align: 'right', className: 'hidden md:table-cell', sort: row => row.cap, render: row => <span className='tabular-nums'>{formatKes(row.cap)}</span> },
        { key: 'spark', label: '30 days', className: 'hidden lg:table-cell', sort: row => row.stock.perf.m1, render: row => <Sparkline values={[...row.stock.spark.slice(0, -1), row.quote.last]} width={84} height={24} /> },
        { key: 'range', label: '52-week range', className: 'hidden 2xl:table-cell', sort: row => (row.stock.high52 - row.stock.low52 > 0 ? (row.quote.last - row.stock.low52) / (row.stock.high52 - row.stock.low52) : 0.5), render: row => <RangeBar low={Math.min(row.stock.low52, row.quote.low)} high={Math.max(row.stock.high52, row.quote.high)} value={row.quote.last} className='w-28' /> }
    ],
    performance: [
        { key: 'last', label: 'Last', align: 'right', sort: row => row.quote.last, render: row => <PriceCell value={row.quote.last} /> },
        { key: 'd1', label: '1D', align: 'right', sort: row => row.quote.changePct, render: row => perfCell(row.quote.changePct) },
        { key: 'w1', label: '1W', align: 'right', sort: row => row.stock.perf.w1, render: row => perfCell(row.stock.perf.w1) },
        { key: 'm1', label: '1M', align: 'right', sort: row => row.stock.perf.m1, render: row => perfCell(row.stock.perf.m1) },
        { key: 'm3', label: '3M', align: 'right', className: 'hidden sm:table-cell', sort: row => row.stock.perf.m3, render: row => perfCell(row.stock.perf.m3) },
        { key: 'm6', label: '6M', align: 'right', className: 'hidden md:table-cell', sort: row => row.stock.perf.m6, render: row => perfCell(row.stock.perf.m6) },
        { key: 'ytd', label: 'YTD', align: 'right', className: 'hidden sm:table-cell', sort: row => row.stock.perf.ytd, render: row => perfCell(row.stock.perf.ytd) },
        { key: 'y1', label: '1Y', align: 'right', className: 'hidden md:table-cell', sort: row => row.stock.perf.y1, render: row => perfCell(row.stock.perf.y1) },
        { key: 'spark', label: '30 days', className: 'hidden lg:table-cell', sort: row => row.stock.perf.m1, render: row => <Sparkline values={[...row.stock.spark.slice(0, -1), row.quote.last]} width={84} height={24} /> }
    ],
    fundamentals: [
        { key: 'last', label: 'Last', align: 'right', sort: row => row.quote.last, render: row => <PriceCell value={row.quote.last} /> },
        { key: 'cap', label: 'Mkt cap', align: 'right', sort: row => row.cap, render: row => <span className='tabular-nums'>{formatKes(row.cap)}</span> },
        { key: 'shares', label: 'Shares', title: 'Shares in issue', align: 'right', className: 'hidden lg:table-cell', sort: row => row.stock.shares, render: row => <span className='tabular-nums text-muted-foreground'>{formatCompact(row.stock.shares)}</span> },
        { key: 'eps', label: 'EPS', title: 'Earnings per share, KES', align: 'right', className: 'hidden sm:table-cell', sort: row => row.stock.eps, render: row => <span className='tabular-nums'>{row.stock.eps !== null ? formatPrice(row.stock.eps) : '-'}</span> },
        { key: 'pe', label: 'P/E', title: 'Price to earnings ratio', align: 'right', sort: row => row.pe, render: row => <span className='tabular-nums'>{row.pe !== null ? formatMultiple(row.pe) : 'n/m'}</span> },
        { key: 'dps', label: 'DPS', title: 'Dividend per share, KES', align: 'right', className: 'hidden md:table-cell', sort: row => row.stock.dps, render: row => <span className='tabular-nums'>{row.stock.dps ? formatPrice(row.stock.dps) : '-'}</span> },
        { key: 'yield', label: 'Div yield', align: 'right', sort: row => row.dividendYield, render: row => <span className='tabular-nums'>{row.dividendYield !== null ? formatPercent(row.dividendYield, 2, false) : '-'}</span> },
        { key: 'payout', label: 'Payout', title: 'Dividend as a share of earnings', align: 'right', className: 'hidden lg:table-cell', sort: row => row.payout, render: row => <span className='tabular-nums text-muted-foreground'>{row.payout !== null ? formatPercent(row.payout, 0, false) : '-'}</span> },
        { key: 'earningsYield', label: 'Earnings yield', align: 'right', className: 'hidden xl:table-cell', sort: row => (row.pe ? 100 / row.pe : null), render: row => <span className='tabular-nums text-muted-foreground'>{row.pe ? formatPercent(100 / row.pe, 1, false) : '-'}</span> }
    ],
    technicals: [
        { key: 'last', label: 'Last', align: 'right', sort: row => row.quote.last, render: row => <PriceCell value={row.quote.last} /> },
        { key: 'rsi', label: 'RSI 14', title: 'Relative strength index over 14 sessions', align: 'right', sort: row => row.stock.rsi, render: row => <RsiCell value={row.stock.rsi} /> },
        { key: 'sma50', label: 'vs 50D', title: 'Distance from the 50-day average', align: 'right', sort: row => row.stock.vsSma50, render: row => perfCell(row.stock.vsSma50) },
        { key: 'sma200', label: 'vs 200D', title: 'Distance from the 200-day average', align: 'right', className: 'hidden sm:table-cell', sort: row => row.stock.vsSma200, render: row => perfCell(row.stock.vsSma200) },
        { key: 'volatility', label: 'Volatility', title: 'Annualised volatility over 12 months', align: 'right', className: 'hidden md:table-cell', sort: row => row.stock.volatility, render: row => <span className='tabular-nums'>{row.stock.volatility !== null ? formatPercent(row.stock.volatility, 1, false) : '-'}</span> },
        { key: 'beta', label: 'Beta', title: 'Sensitivity to the KASI over 12 months', align: 'right', className: 'hidden md:table-cell', sort: row => row.stock.beta, render: row => <span className='tabular-nums'>{row.stock.beta !== null ? formatNumber(row.stock.beta, 2) : '-'}</span> },
        { key: 'avgVolume', label: 'Avg volume', title: 'Average daily volume, 30 sessions', align: 'right', className: 'hidden lg:table-cell', sort: row => row.stock.avgVolume30, render: row => <span className='tabular-nums text-muted-foreground'>{formatCompact(row.stock.avgVolume30)}</span> },
        { key: 'signal', label: 'Signal', className: 'hidden sm:table-cell', sort: row => row.signal, render: row => <SignalBadge signal={row.signal} /> }
    ]
}

const PriceCell = ({ value }: { value: number }): ReactNode => {
    const flash = usePriceFlash(value)

    return <span className={cn('rounded px-1 py-0.5 font-medium tabular-nums text-foreground', flash)}>{formatPrice(value)}</span>
}

const RsiCell = ({ value }: { value: number | null }): ReactNode => {
    if (value === null) return <span className='text-muted-foreground'>-</span>

    return (
        <span className='inline-flex items-center justify-end gap-2'>
            <span className='relative hidden h-1.5 w-12 rounded-full bg-secondary sm:block' aria-hidden='true'>
                <span className='absolute inset-y-0 left-[30%] right-[30%] bg-axis/60' />
                <span className='absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground' style={{ left: `${Math.min(100, Math.max(0, value))}%` }} />
            </span>
            <span className={cn('tabular-nums', value >= 70 ? 'text-down' : value <= 30 ? 'text-up' : 'text-foreground')}>{formatNumber(value, 1)}</span>
        </span>
    )
}

export const SignalBadge = ({ signal }: { signal: string }): ReactNode => {
    const tone = signal === 'Uptrend'
        ? 'bg-up/12 text-up'
        : signal === 'Downtrend'
            ? 'bg-down/12 text-down'
            : signal === 'Overbought' || signal === 'Oversold'
                ? 'bg-primary/12 text-primary'
                : 'bg-secondary text-muted-foreground'

    return <span className={cn('inline-flex whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium', tone)}>{signal}</span>
}

interface EquitiesTableProps {
    stocks: StockRow[]
    sectors: SectorRef[]
    segments: string[]
}

const firstQuery = (value: string | string[] | undefined): string | undefined => (Array.isArray(value) ? value[0] : value)

const EquitiesTable = ({ stocks, sectors, segments }: EquitiesTableProps): ReactNode => {
    const router = useRouter()
    const initialView = firstQuery(router.query.view)
    const initialSector = firstQuery(router.query.sector)

    const [view, setView] = useState<EquitiesView>(VIEWS.some(item => item.value === initialView) ? initialView as EquitiesView : 'prices')
    const [sector, setSector] = useState<string>(initialSector && sectors.some(item => item.slug === initialSector) ? initialSector : 'all')
    const [segment, setSegment] = useState<string>('all')
    const [query, setQuery] = useState('')
    const [watchOnly, setWatchOnly] = useState(false)
    const [sort, setSort] = useState<{ key: string, direction: SortDirection }>({ key: 'cap', direction: 'desc' })
    const watchlist = useWatchlist()

    const specs = useMemo(() => stocks.map(stock => stock.live), [stocks])
    const quotes = useQuotes(specs)

    useEffect(() => {
        const next: Record<string, string> = {}
        if (view !== 'prices') next.view = view
        if (sector !== 'all') next.sector = sector

        const current = router.query
        if ((firstQuery(current.view) ?? '') === (next.view ?? '') && (firstQuery(current.sector) ?? '') === (next.sector ?? '')) return

        void router.replace({ pathname: router.pathname, query: next }, undefined, { shallow: true, scroll: false })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, sector])

    const computed: EquityComputed[] = useMemo(() => stocks.map((stock, index) => {
        const quote = quotes[index]
        const cap = quote.last * stock.shares

        return {
            stock,
            quote,
            cap,
            pe: stock.eps !== null && stock.eps > 0 ? quote.last / stock.eps : null,
            dividendYield: stock.dps !== null && stock.dps > 0 && quote.last > 0 ? (stock.dps / quote.last) * 100 : null,
            payout: stock.dps !== null && stock.eps !== null && stock.eps > 0 ? (stock.dps / stock.eps) * 100 : null,
            signal: signalFor(stock)
        }
    }), [stocks, quotes])

    const columns = COLUMNS[view]
    const activeColumn = columns.find(column => column.key === sort.key)

    const rows = useMemo(() => {
        const text = query.trim().toLowerCase()
        const filtered = computed.filter(row => {
            if (sector !== 'all' && row.stock.sectorSlug !== sector) return false
            if (segment !== 'all' && row.stock.segment !== segment) return false
            if (watchOnly && !watchlist.has(row.stock.id)) return false
            if (text && !row.stock.ticker.toLowerCase().includes(text) && !row.stock.name.toLowerCase().includes(text)) return false

            return true
        })

        const sorter = activeColumn?.sort ?? ((row: EquityComputed) => row.cap)

        return filtered.sort((a, b) => {
            const left = sorter(a)
            const right = sorter(b)
            if (left === null && right === null) return 0
            if (left === null) return 1
            if (right === null) return -1

            const result = typeof left === 'string' || typeof right === 'string'
                ? String(left).localeCompare(String(right))
                : left - right

            return sort.direction === 'asc' ? result : -result
        })
    }, [computed, sector, segment, watchOnly, watchlist, query, activeColumn, sort.direction])

    const toggleSort = (key: string) => {
        setSort(current => (current.key === key
            ? { key, direction: current.direction === 'desc' ? 'asc' : 'desc' }
            : { key, direction: key === 'name' || key === 'signal' ? 'asc' : 'desc' }))
    }

    const resetFilters = () => {
        setQuery('')
        setSector('all')
        setSegment('all')
        setWatchOnly(false)
    }

    const sortName = (row: EquityComputed) => row.stock.ticker
    const nameActive = sort.key === 'name'

    return (
        <div>
            <div className='mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between'>
                <div className='flex flex-wrap items-center gap-2'>
                    <label className='relative flex h-9 w-full items-center sm:w-64'>
                        <span className='sr-only'>Filter by ticker or company</span>
                        <Search aria-hidden='true' className='pointer-events-none absolute left-3 size-4 text-muted-foreground' />
                        <input
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            placeholder='Filter ticker or company'
                            className='h-9 w-full rounded-lg border border-border bg-background pl-9 pr-8 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus-visible:border-ring'
                        />
                        {query && (
                            <button type='button' onClick={() => setQuery('')} aria-label='Clear filter' className='absolute right-2 rounded p-0.5 text-muted-foreground hover:text-foreground'>
                                <X aria-hidden='true' className='size-3.5' />
                            </button>
                        )}
                    </label>

                    <label className='flex h-9 items-center'>
                        <span className='sr-only'>Sector</span>
                        <select
                            value={sector}
                            onChange={event => setSector(event.target.value)}
                            className='h-9 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground outline-none focus-visible:border-ring'
                        >
                            <option value='all'>All sectors</option>
                            {sectors.map(item => <option key={item.id} value={item.slug}>{item.name}</option>)}
                        </select>
                    </label>

                    <label className='flex h-9 items-center'>
                        <span className='sr-only'>Market segment</span>
                        <select
                            value={segment}
                            onChange={event => setSegment(event.target.value)}
                            className='h-9 max-w-56 rounded-lg border border-border bg-background px-3 text-[13px] text-foreground outline-none focus-visible:border-ring'
                        >
                            <option value='all'>All segments</option>
                            {segments.map(item => <option key={item} value={item}>{item}</option>)}
                        </select>
                    </label>

                    <button
                        type='button'
                        aria-pressed={watchOnly}
                        onClick={() => setWatchOnly(value => !value)}
                        className={cn(
                            'inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-[13px] font-medium transition-colors',
                            watchOnly ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Star aria-hidden='true' className={cn('size-4', watchOnly && 'fill-current')} />
                        Watchlist{watchlist.ready && watchlist.items.length ? ` (${watchlist.items.length})` : ''}
                    </button>
                </div>

                <Segmented label='Table view' options={VIEWS} value={view} onChange={next => {
                    setView(next)
                    setSort({ key: next === 'fundamentals' ? 'cap' : next === 'technicals' ? 'rsi' : next === 'performance' ? 'ytd' : 'cap', direction: 'desc' })
                }} size='md' />
            </div>

            <div className='overflow-x-auto rounded-xl border border-border bg-card scroll-thin'>
                <table className='w-full min-w-[640px] text-left text-[13px]'>
                    <caption className='sr-only'>Listed equities, {VIEWS.find(item => item.value === view)?.label} view. Select a column header to sort.</caption>
                    <thead className='sticky top-0 z-10 bg-card'>
                        <tr className='border-b border-border text-[11px] uppercase tracking-[0.06em] text-muted-foreground'>
                            <th scope='col' className='w-9 py-2.5 pl-3'><span className='sr-only'>Watch</span></th>
                            <th scope='col' aria-sort={nameActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'} className='sticky left-0 z-10 bg-card py-2.5 pr-3 font-medium'>
                                <button type='button' onClick={() => toggleSort('name')} className='inline-flex items-center gap-1 uppercase hover:text-foreground'>
                                    Share
                                    {nameActive && (sort.direction === 'asc' ? <ArrowUp aria-hidden='true' className='size-3' /> : <ArrowDown aria-hidden='true' className='size-3' />)}
                                </button>
                            </th>
                            <th scope='col' className='hidden py-2.5 pr-3 font-medium lg:table-cell'>Sector</th>
                            {columns.map(column => {
                                const active = sort.key === column.key

                                return (
                                    <th
                                        key={column.key}
                                        scope='col'
                                        title={column.title}
                                        aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                                        className={cn('py-2.5 pr-3 font-medium last:pr-4', column.align === 'right' && 'text-right', column.className)}
                                    >
                                        <button
                                            type='button'
                                            onClick={() => toggleSort(column.key)}
                                            className={cn('inline-flex items-center gap-1 uppercase hover:text-foreground', active && 'text-foreground')}
                                        >
                                            {column.label}
                                            {active && (sort.direction === 'asc' ? <ArrowUp aria-hidden='true' className='size-3' /> : <ArrowDown aria-hidden='true' className='size-3' />)}
                                        </button>
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {(nameActive
                            ? [...rows].sort((a, b) => (sort.direction === 'asc' ? 1 : -1) * sortName(a).localeCompare(sortName(b)))
                            : rows
                        ).map(row => (
                            <tr key={row.stock.id} className='group border-b border-border/60 transition-colors last:border-0 hover:bg-accent/40'>
                                <td className='py-2 pl-3'>
                                    <WatchButton id={row.stock.id} ticker={row.stock.ticker} />
                                </td>
                                <td className='sticky left-0 z-[1] max-w-56 bg-card py-2 pr-3 transition-colors group-hover:bg-[color-mix(in_oklab,var(--accent)_40%,var(--card))]'>
                                    <Link href={`/stocks/${row.stock.slug}`} className='block min-w-0'>
                                        <span className='flex items-center gap-1.5'>
                                            <span className='font-mono text-[13px] font-semibold text-foreground group-hover:text-primary'>{row.stock.ticker}</span>
                                            {row.stock.inK20 && <span className='rounded border border-border px-1 text-[9px] font-medium text-muted-foreground' title='Kilele 20 constituent'>K20</span>}
                                        </span>
                                        <span className='block truncate text-xs text-muted-foreground'>
                                            <span data-mdlfy-cms={`companies:${row.stock.id}:name`}>{row.stock.name}</span>
                                        </span>
                                    </Link>
                                </td>
                                <td className='hidden max-w-44 py-2 pr-3 text-xs text-muted-foreground lg:table-cell'>
                                    {row.stock.sectorName && row.stock.sectorId
                                        ? <span className='block truncate' data-mdlfy-cms={`sectors:${row.stock.sectorId}:name`}>{row.stock.sectorName}</span>
                                        : '-'}
                                </td>
                                {columns.map(column => (
                                    <td key={column.key} className={cn('py-2 pr-3 last:pr-4', column.align === 'right' && 'text-right', column.className)}>
                                        {column.render(row)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>

                {rows.length === 0 && (
                    <div className='px-6 py-14 text-center'>
                        <p className='text-sm text-foreground'>
                            {watchOnly && watchlist.items.length === 0 ? 'Your watchlist is empty.' : 'No shares match these filters.'}
                        </p>
                        <p className='mt-1 text-[13px] text-muted-foreground'>
                            {watchOnly && watchlist.items.length === 0 ? 'Select the star next to any share to follow it here.' : 'Try a different sector, segment or search term.'}
                        </p>
                        <button type='button' onClick={resetFilters} className='mt-4 inline-flex h-8 items-center rounded-lg border border-border bg-secondary px-3 text-xs font-medium text-foreground hover:bg-accent'>
                            Clear filters
                        </button>
                    </div>
                )}
            </div>

            <p className='mt-3 text-xs text-muted-foreground'>
                Showing {rows.length} of {stocks.length} listed shares. Prices in Kenya shillings. P/E is shown as n/m where earnings are negative.
            </p>
        </div>
    )
}

export default EquitiesTable
